// src/components/pages/MyOrders.jsx
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getColors } from '../../styles/colors';
import { 
  FaHistory, FaCalendarAlt, FaClock, FaArrowLeft,
  FaLeaf, FaFish, FaUtensils, FaTrash, FaShoppingBag,
  FaEye, FaSpinner, FaCheck, FaTimes, FaBox,
  FaRupeeSign, FaEdit, FaPlus, FaMinus, FaCalendarDay,
  FaInfoCircle, FaSave, FaTimesCircle, FaList, FaArrowRight,
  FaImage, FaPhone, FaMapMarkerAlt, FaUser
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionListAsync, cancelSubscriptionAsync } from '../../redux/slices/subscriptionSlice';
import apiService from '../../api/authApi';

const MyOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { user } = useSelector((state) => state.auth || { user: null });
  
  const [subscriptions, setSubscriptions] = useState([]);
  const [cartOrders, setCartOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [products, setProducts] = useState([]);
  const [productMap, setProductMap] = useState({});

  // Meal Off states
  const [showMealOffModal, setShowMealOffModal] = useState(false);
  const [mealOffData, setMealOffData] = useState({
    date: '',
    meal_time: 'Breakfast',
    meal_type: 'Veg'
  });
  const [mealOffLoading, setMealOffLoading] = useState(false);
  const [mealOffMessage, setMealOffMessage] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [mealOffs, setMealOffs] = useState([]);
  const [showMealOffList, setShowMealOffList] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const mealTimes = ['Breakfast', 'Lunch', 'Dinner'];
  const mealTypes = ['Veg', 'Non-Veg'];

  // ✅ BASE URL for images
  const IMAGE_BASE_URL = 'https://admin.thefoodies.in/public/uploads/';

  // ✅ FETCH PRODUCTS FOR DYNAMIC IMAGES
  const fetchProducts = async () => {
    try {
      console.log('🔄 Fetching products for images...');
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️ No token available, skipping product fetch');
        setProducts([]);
        setProductMap({});
        return;
      }
      
      const response = await apiService.getProducts();
      console.log('📦 Products API response:', response);
      
      if (response?.status && response?.data) {
        setProducts(response.data);
        
        // ✅ Create a map for quick lookup with full image URLs
        const map = {};
        response.data.forEach(product => {
          // ✅ Construct full image URL from prod_image
          let imageUrl = null;
          if (product.prod_image) {
            // If image already has full URL, use it as is
            if (product.prod_image.startsWith('http')) {
              imageUrl = product.prod_image;
            } else {
              // Otherwise, prepend the base URL
              imageUrl = IMAGE_BASE_URL + product.prod_image;
            }
          }
          
          map[product.id] = {
            ...product,
            image_url: imageUrl,
            name: product.product_name || `Product #${product.id}`,
            type: product.type || 'Veg',
            price: product.price || 0
          };
        });
        setProductMap(map);
        console.log('✅ Products loaded:', response.data.length, 'items');
        console.log('✅ Product map created:', Object.keys(map).length, 'products');
        
        // Log sample products with images
        const sampleProducts = Object.values(map).slice(0, 3);
        sampleProducts.forEach(p => {
          console.log(`📸 Product ${p.id}: ${p.name} - Image: ${p.image_url || 'No image'}`);
        });
      } else {
        console.warn('⚠️ No products data received');
        setProducts([]);
        setProductMap({});
      }
    } catch (error) {
      console.error('❌ Failed to fetch products:', error);
      setProducts([]);
      setProductMap({});
    }
  };

  // ✅ Get product image with full URL
  const getProductImage = (productId) => {
    if (!productId) {
      return null;
    }
    
    const product = productMap[parseInt(productId)];
    if (product && product.image_url) {
      return product.image_url;
    }
    
    return null;
  };

  // ✅ Get product name
  const getProductName = (productId) => {
    if (!productId) return `Product #${productId}`;
    
    const product = productMap[parseInt(productId)];
    if (product) {
      return product.product_name || product.name || `Product #${productId}`;
    }
    return `Product #${productId}`;
  };

  // ✅ Get product type
  const getProductType = (productId) => {
    if (!productId) return 'Veg';
    
    const product = productMap[parseInt(productId)];
    if (product) {
      return product.type || 'Veg';
    }
    return 'Veg';
  };

  // ✅ Get product price
  const getProductPrice = (productId) => {
    if (!productId) return 0;
    
    const product = productMap[parseInt(productId)];
    if (product) {
      return parseFloat(product.price) || 0;
    }
    return 0;
  };

  // ✅ FETCH ALL ORDERS
  const fetchAllOrders = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // ✅ First fetch products for images
      await fetchProducts();

      // 1. Fetch subscription orders
      try {
        const subResult = await dispatch(getSubscriptionListAsync()).unwrap();
        if (subResult?.data) {
          const subscriptionsWithMealOffs = subResult.data.map((sub) => {
            const mealOffs = sub.meal_offs || sub.mealOffs || sub.meal_off || [];
            return {
              ...sub,
              mealOffs: Array.isArray(mealOffs) ? mealOffs : [],
              current_total_after_offs: sub.current_total_after_offs || sub.projected_total || sub.total || 0,
              projected_total: sub.projected_total || sub.total || 0,
              outstanding_amount: sub.outstanding_amount || 0
            };
          });
          setSubscriptions(subscriptionsWithMealOffs);
          console.log('✅ Subscriptions loaded:', subscriptionsWithMealOffs);
        }
      } catch (subError) {
        console.error('❌ Failed to fetch subscriptions:', subError);
      }

      // 2. Fetch cart order history
      try {
        const response = await apiService.getOrderHistory();
        console.log('📦 Cart orders response:', response);
        if (response?.status && response?.data) {
          const orders = response.data.map(order => ({
            ...order,
            order_id: order.order_id || order.id,
            total: order.total_amount || order.total || 0,
            status: order.order_status || order.status || 'pending',
            payment: order.payment_status || order.payment || 'pending',
            created_at: order.created_at,
            items: order.items || [],
            customer_address: order.customer_address,
            customer_phone: order.customer_phone,
            customer_name: order.customer_name,
            order_number: order.order_number,
            advance_booking_date: order.advance_booking_date
          }));
          setCartOrders(orders);
          console.log('✅ Cart orders loaded:', orders);
        } else {
          setCartOrders([]);
        }
      } catch (cartError) {
        console.error('❌ Failed to fetch cart orders:', cartError);
        setCartOrders([]);
      }

      // 3. Fetch custom orders from localStorage
      try {
        const savedOrders = localStorage.getItem('customOrders');
        if (savedOrders) {
          const parsed = JSON.parse(savedOrders);
          setCustomOrders(Array.isArray(parsed) ? parsed : []);
        } else {
          setCustomOrders([]);
        }
      } catch (localError) {
        console.error('❌ Failed to fetch custom orders:', localError);
        setCustomOrders([]);
      }
      
    } catch (error) {
      console.error('❌ Failed to fetch orders:', error);
      setError('Failed to load orders. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOrders();
  }, [refreshKey]);

  // ========== MEAL OFF FUNCTIONS ==========
  
  // ✅ OPEN MEAL OFF MODAL
  const handleOpenMealOffModal = async (subscription) => {
    if (subscription.status?.toLowerCase() !== 'active' && subscription.status?.toLowerCase() !== 'pending_payment') {
      setMealOffMessage({ 
        type: 'error', 
        text: '❌ This subscription is not active. You can only modify active subscriptions.'
      });
      setTimeout(() => {
        setMealOffMessage(null);
      }, 3000);
      return;
    }
    
    setCurrentSubscription(subscription);
    setMealOffData({
      date: '',
      meal_time: 'Breakfast',
      meal_type: 'Veg'
    });
    setMealOffMessage(null);
    setMealOffLoading(false);
    setCurrentMonth(new Date());
    
    try {
      console.log('🔄 Fetching fresh subscription data for meal offs...');
      const freshSubResult = await dispatch(getSubscriptionListAsync()).unwrap();
      console.log('✅ Fresh subscription data:', freshSubResult);
      
      if (freshSubResult?.data) {
        const updatedSub = freshSubResult.data.find(sub => sub.id === subscription.id);
        if (updatedSub) {
          const freshMealOffs = updatedSub.meal_offs || updatedSub.mealOffs || updatedSub.meal_off || [];
          
          console.log('✅ Fresh meal offs loaded:', freshMealOffs);
          
          setMealOffs(Array.isArray(freshMealOffs) ? freshMealOffs : []);
          
          setSubscriptions(prev => prev.map(sub => {
            if (sub.id === subscription.id) {
              return {
                ...sub,
                mealOffs: freshMealOffs,
                current_total_after_offs: updatedSub.current_total_after_offs || updatedSub.projected_total || 0,
                outstanding_amount: updatedSub.outstanding_amount || 0
              };
            }
            return sub;
          }));
          
          setCurrentSubscription({
            ...subscription,
            mealOffs: freshMealOffs,
            current_total_after_offs: updatedSub.current_total_after_offs || updatedSub.projected_total || 0,
            outstanding_amount: updatedSub.outstanding_amount || 0
          });
        }
      }
    } catch (error) {
      console.error('❌ Failed to refresh meal offs:', error);
      const existingMealOffs = subscription.mealOffs || subscription.meal_offs || subscription.meal_off || [];
      setMealOffs(Array.isArray(existingMealOffs) ? existingMealOffs : []);
    }
    
    setShowMealOffModal(true);
  };

  // ✅ MARK MEAL OFF
  const handleMarkMealOff = async () => {
    if (!mealOffData.date) {
      setMealOffMessage({ type: 'error', text: 'Please select a date.' });
      return;
    }

    setMealOffLoading(true);
    setMealOffMessage(null);

    try {
      console.log('📤 Marking meal off:', {
        subscriptionId: currentSubscription.id,
        date: mealOffData.date,
        meal_time: mealOffData.meal_time,
        meal_type: mealOffData.meal_type
      });

      const response = await apiService.markMealOff(
        currentSubscription.id,
        mealOffData.date,
        mealOffData.meal_time,
        mealOffData.meal_type
      );

      console.log('✅ Meal off response:', response);

      if (response.status) {
        const newTotal = response.data?.current_total_after_offs || 0;
        const outstanding = response.data?.outstanding_amount || 0;
        
        const newMealOff = {
          date: mealOffData.date,
          meal_time: mealOffData.meal_time,
          meal_type: mealOffData.meal_type
        };
        
        setMealOffMessage({ 
          type: 'success', 
          text: `✅ Meal marked off! New total: ₹${newTotal}`,
          data: response.data
        });
        
        setMealOffs(prev => {
          const updated = [...prev, newMealOff];
          console.log('✅ Updated mealOffs state:', updated);
          return updated;
        });
        
        setSubscriptions(prev => prev.map(sub => {
          if (sub.id === currentSubscription.id) {
            const updatedSub = {
              ...sub,
              current_total_after_offs: newTotal,
              outstanding_amount: outstanding,
              mealOffs: [...(sub.mealOffs || []), newMealOff]
            };
            console.log('✅ Updated subscription:', updatedSub);
            return updatedSub;
          }
          return sub;
        }));
        
        setCurrentSubscription(prev => {
          const updated = {
            ...prev,
            current_total_after_offs: newTotal,
            outstanding_amount: outstanding,
            mealOffs: [...(prev?.mealOffs || []), newMealOff]
          };
          console.log('✅ Updated current subscription:', updated);
          return updated;
        });
        
        setMealOffData({
          date: '',
          meal_time: 'Breakfast',
          meal_type: 'Veg'
        });
        
        setRefreshKey(prev => prev + 1);
        
        setTimeout(() => {
          setMealOffMessage(null);
          setShowMealOffModal(false);
          setTimeout(() => {
            handleOpenMealOffModal(currentSubscription);
          }, 300);
        }, 2000);
        
      } else {
        throw new Error(response.message || 'Failed to mark meal off');
      }
    } catch (error) {
      console.error('❌ Failed to mark meal off:', error);
      setMealOffMessage({ 
        type: 'error', 
        text: error.message || 'Failed to mark meal off. Please try again.'
      });
    } finally {
      setMealOffLoading(false);
    }
  };

  // ✅ REMOVE MEAL OFF
  const handleRemoveMealOff = async (date, mealTime, mealType) => {
    if (!window.confirm(`Remove meal off for ${date} - ${mealTime} (${mealType})?`)) {
      return;
    }

    try {
      const result = await apiService.removeMealOff(
        currentSubscription.id,
        date,
        mealTime,
        mealType
      );
      
      console.log('✅ Remove meal off response:', result);
      
      if (result.status) {
        setMealOffs(prev => prev.filter(
          off => !(off.date === date && off.meal_time === mealTime && off.meal_type === mealType)
        ));
        
        const freshSubResult = await dispatch(getSubscriptionListAsync()).unwrap();
        if (freshSubResult?.data) {
          const updatedSub = freshSubResult.data.find(sub => sub.id === currentSubscription.id);
          if (updatedSub) {
            const freshMealOffs = updatedSub.meal_offs || updatedSub.mealOffs || updatedSub.meal_off || [];
            
            setSubscriptions(prev => prev.map(sub => {
              if (sub.id === currentSubscription.id) {
                return {
                  ...sub,
                  current_total_after_offs: updatedSub.current_total_after_offs || updatedSub.projected_total || 0,
                  outstanding_amount: updatedSub.outstanding_amount || 0,
                  mealOffs: freshMealOffs
                };
              }
              return sub;
            }));
            
            setCurrentSubscription(prev => ({
              ...prev,
              current_total_after_offs: updatedSub.current_total_after_offs || updatedSub.projected_total || 0,
              outstanding_amount: updatedSub.outstanding_amount || 0,
              mealOffs: freshMealOffs
            }));
            
            setMealOffs(Array.isArray(freshMealOffs) ? freshMealOffs : []);
          }
        }
        
        setMealOffMessage({ 
          type: 'success', 
          text: result.message || 'Meal off removed successfully!'
        });
        
        setRefreshKey(prev => prev + 1);
        
        setTimeout(() => {
          setMealOffMessage(null);
        }, 3000);
      }
    } catch (error) {
      console.error('❌ Failed to remove meal off:', error);
      setMealOffMessage({ 
        type: 'error', 
        text: error.message || 'Failed to remove meal off. Please try again.'
      });
    }
  };

  // ========== CALENDAR FUNCTIONS ==========
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDay };
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const isToday = (day) => {
    const today = new Date();
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === today.toDateString();
  };

  const isDateSkipped = (dateStr, mealTime, mealType) => {
    const result = mealOffs.some(off => 
      off.date === dateStr && 
      off.meal_time === mealTime && 
      off.meal_type?.toLowerCase() === mealType?.toLowerCase()
    );
    return result;
  };

  const getDateStr = (month, day) => {
    const year = month.getFullYear();
    const monthNum = String(month.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${monthNum}-${dayStr}`;
  };

  const getStatusColor = (status) => {
    if (!status) return '#6B7280';
    const statusLower = status.toLowerCase();
    if (statusLower === 'cancelled') return '#F59E0B';
    switch (statusLower) {
      case 'active':
      case 'completed':
      case 'paid':
      case 'confirmed':
      case 'delivered':
        return '#22C55E';
      case 'pending':
      case 'pending_payment':
      case 'placed':
        return '#F59E0B';
      case 'processing':
        return '#3B82F6';
      default:
        return '#6B7280';
    }
  };

  const getStatusText = (status) => {
    if (!status) return 'Unknown';
    const statusLower = status.toLowerCase();
    if (statusLower === 'cancelled') return 'Pending';
    switch (statusLower) {
      case 'pending_payment': return 'Pending Payment';
      case 'pending': return 'Pending';
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'confirmed': return 'Confirmed';
      case 'placed': return 'Placed';
      case 'processing': return 'Processing';
      case 'delivered': return 'Delivered';
      default: return status;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const getMealIcon = (mealTime) => {
    const icons = {
      'Breakfast': '🌅',
      'Lunch': '☀️',
      'Dinner': '🌙'
    };
    return icons[mealTime] || '🍽️';
  };

  const getItemTypeIcon = (item) => {
    const type = item.item_type || item.type || 'product';
    const productType = getProductType(item.product_id);
    if (productType === 'Veg') {
      return <FaLeaf className="text-green-500" />;
    } else if (productType === 'Non-Veg') {
      return <FaFish className="text-red-500" />;
    }
    if (type === 'veg' || type === 'Veg') {
      return <FaLeaf className="text-green-500" />;
    } else if (type === 'nonveg' || type === 'NonVeg' || type === 'Non-Veg') {
      return <FaFish className="text-red-500" />;
    }
    return <FaUtensils className="text-yellow-500" />;
  };

  const handleViewOrder = (order, type) => {
    setSelectedOrder({ ...order, orderType: type });
    setShowOrderDetail(true);
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    try {
      await dispatch(cancelSubscriptionAsync(subscriptionId)).unwrap();
      alert('✅ Subscription cancelled successfully!');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('❌ Failed to cancel subscription:', error);
      alert('❌ Failed to cancel subscription. Please try again.');
    }
  };

  const getPaymentColor = (payment) => {
    const paymentLower = payment?.toLowerCase() || '';
    if (paymentLower === 'paid' || paymentLower === 'completed') {
      return '#22C55E';
    } else if (paymentLower === 'pending' || paymentLower === 'pending_payment') {
      return '#F59E0B';
    } else if (paymentLower === 'failed') {
      return '#EF4444';
    }
    return '#6B7280';
  };

  const getAllOrders = () => {
    const all = [];
    
    if (cartOrders && cartOrders.length > 0) {
      cartOrders.forEach(order => {
        all.push({
          ...order,
          orderType: 'cart',
          displayName: `Order #${order.order_id}`,
          date: order.created_at,
          total: parseFloat(order.total) || 0,
          status: order.status || 'pending',
          payment: order.payment || 'pending',
          source: 'cart',
          items: order.items || []
        });
      });
    }

    if (subscriptions && subscriptions.length > 0) {
      subscriptions.forEach(sub => {
        const totalAmount = sub.current_total_after_offs || sub.projected_total || sub.total || 0;
        all.push({
          ...sub,
          orderType: 'subscription',
          displayName: `Monthly Subscription #${sub.id}`,
          date: sub.start_date || sub.created_at,
          total: totalAmount,
          projected_total: sub.projected_total || sub.total || 0,
          current_total_after_offs: sub.current_total_after_offs || 0,
          status: sub.status || 'pending',
          source: 'subscription',
          items: sub.items || [],
          mealOffs: sub.mealOffs || []
        });
      });
    }

    if (customOrders && customOrders.length > 0) {
      customOrders.forEach(order => {
        all.push({
          ...order,
          orderType: 'custom',
          displayName: `Custom Order #${order.order_id || order.id}`,
          date: order.created_at || order.date,
          total: order.total_amount || order.total || 0,
          status: order.payment_status || order.status || 'pending',
          source: 'custom',
          items: order.items || []
        });
      });
    }

    all.sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);
      return dateB - dateA;
    });
    return all;
  };

  const getFilteredOrders = () => {
    const all = getAllOrders();
    if (activeTab === 'all') return all;
    if (activeTab === 'cart') return all.filter(o => o.orderType === 'cart');
    if (activeTab === 'subscription') return all.filter(o => o.orderType === 'subscription');
    if (activeTab === 'custom') return all.filter(o => o.orderType === 'custom');
    return all;
  };

  const filteredOrders = getFilteredOrders();

  // ========== ORDER DETAIL VIEW ==========
  const OrderDetailView = () => {
    if (!selectedOrder) return null;

    const items = selectedOrder.items || [];
    const isCartOrder = selectedOrder.orderType === 'cart';
    const isSubscription = selectedOrder.orderType === 'subscription';
    const mealOffs = selectedOrder.mealOffs || [];
    const totalAmount = parseFloat(selectedOrder.current_total_after_offs || selectedOrder.total || 0);
    const originalTotal = parseFloat(selectedOrder.projected_total || selectedOrder.total || 0);
    const hasDiscount = originalTotal > totalAmount;

    return (
      <div className="space-y-4">
        <button
          onClick={() => { setShowOrderDetail(false); setSelectedOrder(null); }}
          className="flex items-center gap-2 text-sm font-medium transition-all hover:scale-105"
          style={{ color: colors.primary }}
        >
          <FaArrowLeft /> Back to Orders
        </button>

        <div className="rounded-2xl p-6" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
              {isCartOrder ? '🛒 Cart Order' : isSubscription ? '📆 Monthly Subscription' : '📅 Custom Order'}
            </h2>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: getStatusColor(selectedOrder.status) }}>
                {getStatusText(selectedOrder.status)}
              </span>
              {isCartOrder && selectedOrder.payment && (
                <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ 
                  backgroundColor: getPaymentColor(selectedOrder.payment) + '20',
                  color: getPaymentColor(selectedOrder.payment)
                }}>
                  {selectedOrder.payment}
                </span>
              )}
            </div>
          </div>

          {/* Order Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span style={{ color: colors.textSecondary }}>Order ID</span>
              <span style={{ color: colors.text }}>#{selectedOrder.order_id || selectedOrder.id || 'N/A'}</span>
            </div>
            {selectedOrder.order_number && (
              <div className="flex justify-between">
                <span style={{ color: colors.textSecondary }}>Order Number</span>
                <span style={{ color: colors.text }}>{selectedOrder.order_number}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span style={{ color: colors.textSecondary }}>Date</span>
              <span style={{ color: colors.text }}>{formatDate(selectedOrder.created_at || selectedOrder.date)}</span>
            </div>
            {selectedOrder.advance_booking_date && (
              <div className="flex justify-between">
                <span style={{ color: colors.textSecondary }}>Booking Date</span>
                <span style={{ color: colors.text }}>{formatDate(selectedOrder.advance_booking_date)}</span>
              </div>
            )}
            {selectedOrder.customer_name && (
              <div className="flex justify-between">
                <span style={{ color: colors.textSecondary }}>Customer</span>
                <span style={{ color: colors.text }}>{selectedOrder.customer_name}</span>
              </div>
            )}
            {selectedOrder.customer_phone && (
              <div className="flex justify-between">
                <span style={{ color: colors.textSecondary }}>Phone</span>
                <span style={{ color: colors.text }}>{selectedOrder.customer_phone}</span>
              </div>
            )}
            {selectedOrder.customer_address && (
              <div className="flex justify-between col-span-2">
                <span style={{ color: colors.textSecondary }}>Address</span>
                <span style={{ color: colors.text }} className="text-right">{selectedOrder.customer_address}</span>
              </div>
            )}
            <div className="flex justify-between col-span-2 pt-2 border-t" style={{ borderColor: colors.border }}>
              <span style={{ color: colors.textSecondary }}>Total Amount</span>
              <div className="text-right">
                <span className="text-xl font-bold" style={{ color: colors.primary }}>₹{totalAmount}</span>
                {hasDiscount && (
                  <p className="text-xs line-through" style={{ color: colors.textSecondary }}>
                    ₹{originalTotal}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Items Section with Dynamic Images */}
          {items && items.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text }}>
                <FaShoppingBag className="text-sm" /> Items ({items.length})
              </h4>
              <div className="space-y-3">
                {items.map((item, idx) => {
                  const productId = parseInt(item.product_id);
                  const itemType = item.item_type || 'product';
                  const isFoodSchedule = itemType === 'food_schedule';
                  const productImage = getProductImage(productId);
                  const productName = getProductName(productId);
                  const productType = getProductType(productId);
                  
                  return (
                    <div 
                      key={idx} 
                      className="flex items-center gap-3 p-3 rounded-lg"
                      style={{ backgroundColor: colors.inputBackground }}
                    >
                      {/* Product Image - Dynamic from server */}
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                        {productImage ? (
                          <img 
                            src={productImage}
                            alt={productName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.log(`❌ Failed to load image for product ${productId}:`, productImage);
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(productName)}&background=FF6B35&color=fff&size=80`;
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: colors.primary + '10' }}>
                            <FaImage className="text-2xl" style={{ color: colors.primary + '50' }} />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm" style={{ color: colors.text }}>
                            {productName}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ 
                            backgroundColor: productType === 'Veg' ? '#22C55E20' : '#EF444420',
                            color: productType === 'Veg' ? '#22C55E' : '#EF4444'
                          }}>
                            {productType === 'Veg' ? <FaLeaf className="inline mr-1" /> : <FaFish className="inline mr-1" />}
                            {productType}
                          </span>
                          {isFoodSchedule && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ 
                              backgroundColor: colors.primary + '20',
                              color: colors.primary
                            }}>
                              Tiffin Schedule
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs" style={{ color: colors.textSecondary }}>
                          <span>Qty: {item.quantity || 1}</span>
                          <span>×</span>
                          <span>₹{parseFloat(item.price || 0).toFixed(2)}</span>
                          <span className="font-medium" style={{ color: colors.primary }}>
                            ₹{parseFloat(item.subtotal || 0).toFixed(2)}
                          </span>
                        </div>
                        {isFoodSchedule && item.booking_date && (
                          <p className="text-[10px]" style={{ color: colors.textSecondary }}>
                            📅 {formatDate(item.booking_date)}
                          </p>
                        )}
                      </div>
                      
                      <div className="text-[10px] text-right" style={{ color: colors.textSecondary }}>
                        ID: {item.product_id}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Meal Offs Section */}
          {isSubscription && mealOffs.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: colors.text }}>
                <FaCalendarDay className="text-sm" style={{ color: '#F59E0B' }} />
                Skipped Meals ({mealOffs.length})
              </h4>
              <div className="space-y-2">
                {mealOffs.map((mealOff, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 rounded-lg"
                    style={{ 
                      backgroundColor: '#FEF3C7',
                      border: '1px solid #FCD34D'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{getMealIcon(mealOff.meal_time)}</span>
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#92400E' }}>
                          {formatDateFull(mealOff.date)}
                        </p>
                        <div className="flex items-center gap-2 text-xs" style={{ color: '#92400E' }}>
                          <span>{mealOff.meal_time}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            {mealOff.meal_type?.toLowerCase() === 'veg' ? (
                              <FaLeaf className="text-green-600 text-[10px]" />
                            ) : (
                              <FaFish className="text-red-600 text-[10px]" />
                            )}
                            {mealOff.meal_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-3" style={{ borderColor: colors.border }}>
            {isSubscription && selectedOrder.status !== 'cancelled' && (
              <button
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: colors.primary + '15',
                  color: colors.primary,
                  border: `1px solid ${colors.primary}30`
                }}
                onClick={() => {
                  setShowOrderDetail(false);
                  handleOpenMealOffModal(selectedOrder);
                }}
              >
                <FaEdit className="text-sm" /> Modify Subscription
              </button>
            )}
            
            {isSubscription && selectedOrder.status !== 'cancelled' && (
              <button
                onClick={() => handleCancelSubscription(selectedOrder.id)}
                className="flex-1 py-2.5 rounded-xl font-medium text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
              >
                <FaTrash className="text-sm" /> Cancel Subscription
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========== MEAL OFF MODAL COMPONENT ==========
  const MealOffModal = () => {
    if (!showMealOffModal) return null;

    const { daysInMonth, firstDay } = getDaysInMonth(currentMonth);
    const monthName = currentMonth.toLocaleString('default', { month: 'long' });
    const year = currentMonth.getFullYear();

    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-10 h-10" />);
    }

    const startDate = currentSubscription?.start_date ? new Date(currentSubscription.start_date) : null;
    const endDate = currentSubscription?.end_date ? new Date(currentSubscription.end_date) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const currentTotal = currentSubscription?.current_total_after_offs || 
                        currentSubscription?.projected_total || 
                        currentSubscription?.total || 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = getDateStr(currentMonth, day);
      const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      dateObj.setHours(0, 0, 0, 0);
      
      let isInRange = true;
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        isInRange = dateObj >= start && dateObj <= end;
      }
      
      const isPastDate = dateObj < today;
      const isSkipped = isDateSkipped(dateStr, mealOffData.meal_time, mealOffData.meal_type);
      const isSelectable = isInRange && !isPastDate && !isSkipped;
      
      days.push(
        <button
          key={day}
          className={`w-10 h-10 rounded-lg text-sm font-medium transition-all hover:scale-105 relative ${
            isSkipped ? 'opacity-50 cursor-not-allowed' : ''
          } ${!isInRange || isPastDate ? 'opacity-30 cursor-not-allowed' : ''} ${
            isSelectable ? 'hover:shadow-md' : ''
          } ${mealOffData.date === dateStr && isSelectable ? 'border-2' : ''}`}
          style={{
            backgroundColor: isSkipped ? '#FEF3C7' : 
                           mealOffData.date === dateStr ? colors.primary : 
                           isInRange && !isPastDate ? colors.inputBackground : 'transparent',
            color: mealOffData.date === dateStr ? '#FFFFFF' : 
                   isSkipped ? '#92400E' :
                   isInRange && !isPastDate ? colors.text : colors.textSecondary,
            borderColor: mealOffData.date === dateStr ? colors.primary : 
                         isSkipped ? '#FCD34D' : colors.border,
            cursor: isSelectable ? 'pointer' : 'not-allowed'
          }}
          onClick={() => {
            if (isSelectable) {
              setMealOffData({ ...mealOffData, date: dateStr });
            }
          }}
          disabled={!isSelectable}
        >
          {day}
          {isSkipped && (
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-yellow-500" />
          )}
          {isToday(day) && !isSkipped && isInRange && !isPastDate && (
            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.primary }} />
          )}
        </button>
      );
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
        <div 
          className="rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: colors.text }}>
                <FaEdit style={{ color: colors.primary }} />
                Modify Subscription
              </h3>
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                Subscription #{currentSubscription?.id} - Skip Meals
              </p>
              {currentSubscription?.start_date && currentSubscription?.end_date && (
                <p className="text-xs mt-0.5" style={{ color: colors.textSecondary }}>
                  📅 {formatDate(currentSubscription.start_date)} - {formatDate(currentSubscription.end_date)}
                </p>
              )}
              <p className="text-xs font-bold mt-1" style={{ color: colors.primary }}>
                Current Total: ₹{currentTotal}
              </p>
            </div>
            <button 
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ backgroundColor: colors.inputBackground }}
              onClick={() => {
                setShowMealOffModal(false);
                setMealOffMessage(null);
                setCurrentSubscription(null);
              }}
            >
              <FaTimesCircle style={{ color: colors.text }} />
            </button>
          </div>

          {mealOffMessage && (
            <div className={`p-3 rounded-xl mb-4 text-sm flex items-center gap-2 ${
              mealOffMessage.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
            }`}>
              {mealOffMessage.type === 'success' ? <FaCheck /> : <FaTimes />}
              {mealOffMessage.text}
              {mealOffMessage.data && (
                <span className="ml-2 text-xs font-medium">
                  New Total: ₹{mealOffMessage.data.current_total_after_offs}
                </span>
              )}
            </div>
          )}

          {/* Calendar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <button
                className="p-1.5 rounded-lg transition-all hover:scale-105"
                style={{ backgroundColor: colors.inputBackground }}
                onClick={() => navigateMonth(-1)}
              >
                <FaArrowLeft className="text-xs" style={{ color: colors.text }} />
              </button>
              <span className="text-sm font-medium" style={{ color: colors.text }}>
                {monthName} {year}
              </span>
              <button
                className="p-1.5 rounded-lg transition-all hover:scale-105"
                style={{ backgroundColor: colors.inputBackground }}
                onClick={() => navigateMonth(1)}
              >
                <FaArrowRight className="text-xs" style={{ color: colors.text }} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center text-[10px] font-medium" style={{ color: colors.textSecondary }}>
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days}
            </div>

            <div className="flex items-center gap-4 mt-2 pt-2 border-t text-[10px]" style={{ borderColor: colors.border }}>
              <div className="flex items-center gap-1" style={{ color: colors.textSecondary }}>
                <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.primary }} />
                Selected
              </div>
              <div className="flex items-center gap-1" style={{ color: colors.textSecondary }}>
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }} />
                Skipped
              </div>
              <div className="flex items-center gap-1" style={{ color: colors.textSecondary }}>
                <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.primary, opacity: 0.3 }} />
                Available
              </div>
            </div>
          </div>

          {/* Meal Time Selection */}
          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Select Meal Time to Skip
            </label>
            <div className="grid grid-cols-3 gap-2">
              {mealTimes.map((time) => (
                <button
                  key={time}
                  className={`py-2 px-3 rounded-xl text-xs font-medium transition-all hover:scale-105 ${
                    mealOffData.meal_time === time ? 'border-2' : 'border'
                  }`}
                  style={{
                    borderColor: mealOffData.meal_time === time ? colors.primary : colors.border,
                    backgroundColor: mealOffData.meal_time === time ? colors.primary + '20' : colors.inputBackground,
                    color: mealOffData.meal_time === time ? colors.primary : colors.text
                  }}
                  onClick={() => setMealOffData({ ...mealOffData, meal_time: time })}
                >
                  <span className="text-base block">{getMealIcon(time)}</span>
                  {time}
                </button>
              ))}
            </div>
          </div>

          {/* Meal Type Selection */}
          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Select Meal Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {mealTypes.map((type) => (
                <button
                  key={type}
                  className={`py-2 px-3 rounded-xl text-xs font-medium transition-all hover:scale-105 flex items-center justify-center gap-2 ${
                    mealOffData.meal_type === type ? 'border-2' : 'border'
                  }`}
                  style={{
                    borderColor: mealOffData.meal_type === type ? colors.primary : colors.border,
                    backgroundColor: mealOffData.meal_type === type ? colors.primary + '20' : colors.inputBackground,
                    color: mealOffData.meal_type === type ? colors.primary : colors.text
                  }}
                  onClick={() => setMealOffData({ ...mealOffData, meal_type: type })}
                >
                  {type === 'Veg' ? (
                    <FaLeaf className={mealOffData.meal_type === type ? 'text-green-500' : 'text-gray-400'} />
                  ) : (
                    <FaFish className={mealOffData.meal_type === type ? 'text-red-500' : 'text-gray-400'} />
                  )}
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Existing Meal Offs List */}
          {mealOffs.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium flex items-center gap-2" style={{ color: colors.text }}>
                  <FaList className="text-xs" />
                  Skipped Meals ({mealOffs.length})
                </h4>
                <button
                  className="text-xs hover:underline"
                  style={{ color: colors.primary }}
                  onClick={() => setShowMealOffList(!showMealOffList)}
                >
                  {showMealOffList ? 'Hide' : 'Show'}
                </button>
              </div>
              {showMealOffList && (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {mealOffs.map((mealOff, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 rounded-lg"
                      style={{ 
                        backgroundColor: '#FEF3C7',
                        border: '1px solid #FCD34D'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{getMealIcon(mealOff.meal_time)}</span>
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#92400E' }}>
                            {formatDateFull(mealOff.date)}
                          </p>
                          <div className="flex items-center gap-1 text-[10px]" style={{ color: '#92400E' }}>
                            <span>{mealOff.meal_time}</span>
                            <span>•</span>
                            <span className="flex items-center gap-0.5">
                              {mealOff.meal_type?.toLowerCase() === 'veg' ? (
                                <FaLeaf className="text-green-600 text-[8px]" />
                              ) : (
                                <FaFish className="text-red-600 text-[8px]" />
                              )}
                              {mealOff.meal_type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="p-1 rounded-full transition-all hover:scale-110"
                        style={{ color: '#EF4444' }}
                        onClick={() => handleRemoveMealOff(mealOff.date, mealOff.meal_time, mealOff.meal_type)}
                      >
                        <FaTrash className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected Summary */}
          {mealOffData.date && (
            <div className="p-3 rounded-xl mb-4" style={{ 
              backgroundColor: colors.primary + '10',
              border: `1px solid ${colors.primary}25`
            }}>
              <p className="text-xs font-medium" style={{ color: colors.text }}>
                <FaInfoCircle className="inline mr-1.5" />
                You're about to skip:
              </p>
              <div className="flex items-center gap-2 mt-1 text-sm" style={{ color: colors.primary }}>
                <span>{formatDateFull(mealOffData.date)}</span>
                <span>•</span>
                <span>{getMealIcon(mealOffData.meal_time)} {mealOffData.meal_time}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  {mealOffData.meal_type === 'Veg' ? (
                    <FaLeaf className="text-green-500" />
                  ) : (
                    <FaFish className="text-red-500" />
                  )}
                  {mealOffData.meal_type}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-[1.02]"
              style={{ 
                backgroundColor: colors.inputBackground,
                color: colors.text,
                border: `1px solid ${colors.border}`
              }}
              onClick={() => {
                setShowMealOffModal(false);
                setMealOffMessage(null);
                setCurrentSubscription(null);
              }}
            >
              Close
            </button>
            <button
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: mealOffData.date ? colors.primary : colors.border,
                opacity: mealOffData.date ? 1 : 0.5,
                cursor: mealOffData.date ? 'pointer' : 'not-allowed'
              }}
              onClick={handleMarkMealOff}
              disabled={!mealOffData.date || mealOffLoading}
            >
              {mealOffLoading ? (
                <><FaSpinner className="animate-spin" /> Skipping...</>
              ) : (
                <><FaPlus /> Skip Meal</>
              )}
            </button>
          </div>

          <p className="text-[10px] text-center mt-4" style={{ color: colors.textSecondary + '80' }}>
            <FaInfoCircle className="inline mr-1" />
            Skipping a meal will reduce your total bill. You can undo this anytime.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="relative pt-6 pb-4 px-4 border-b"
           style={{
             background: `linear-gradient(180deg, ${colors.primary}20 0%, ${colors.background} 100%)`,
             borderColor: colors.border
           }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: colors.text }}>
              <FaHistory style={{ color: colors.primary }} />
              My Orders
            </h1>
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              <span style={{ color: colors.textSecondary }}>📱 {user?.phone || '6295028775'}</span>
            </p>
          </div>
          <button
            onClick={() => navigate('/profile')}
            className="px-4 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-105"
            style={{ 
              backgroundColor: colors.inputBackground,
              color: colors.text,
              border: `1px solid ${colors.border}`
            }}
          >
            Back to Profile
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 max-w-4xl">
        {showOrderDetail ? (
          <OrderDetailView />
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-6 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 overflow-x-auto">
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'all' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'all' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'all' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('all')}
              >
                All Orders
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'cart' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'cart' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'cart' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('cart')}
              >
                <FaShoppingBag className="inline mr-1.5" /> Cart Orders
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'subscription' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'subscription' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'subscription' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('subscription')}
              >
                <FaCalendarAlt className="inline mr-1.5" /> Monthly
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'custom' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'custom' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'custom' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('custom')}
              >
                <FaClock className="inline mr-1.5" /> Custom
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-3 rounded-lg text-sm flex items-start gap-2"
                   style={{ 
                     backgroundColor: '#FEF2F2',
                     color: '#DC2626',
                     border: '1px solid #FECACA'
                   }}>
                <span className="mt-0.5">⚠️</span>
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-xs font-medium hover:underline"
                  style={{ color: '#DC2626' }}
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Orders List */}
            {isLoading ? (
              <div className="text-center py-12">
                <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: colors.primary }} />
                <p className="mt-4" style={{ color: colors.textSecondary }}>Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>No Orders Yet</h3>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  {activeTab === 'all' 
                    ? "You haven't placed any orders yet." 
                    : activeTab === 'cart'
                    ? "You don't have any cart orders."
                    : activeTab === 'subscription' 
                    ? "You don't have any monthly subscriptions." 
                    : "You don't have any custom date orders."}
                </p>
                <button
                  onClick={() => navigate('/cart-service')}
                  className="mt-4 px-6 py-2 rounded-full text-white font-medium transition-all hover:scale-105"
                  style={{ backgroundColor: colors.primary }}
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map((order, index) => {
                  const orderItems = order.items || [];
                  const isCartOrder = order.orderType === 'cart';
                  const isSubscription = order.orderType === 'subscription';
                  const mealOffs = order.mealOffs || [];
                  const totalAmount = parseFloat(order.current_total_after_offs || order.total || 0);
                  const originalTotal = parseFloat(order.projected_total || order.total || 0);
                  const hasDiscount = originalTotal > totalAmount;
                  
                  return (
                    <div
                      key={index}
                      className="rounded-xl p-4 cursor-pointer transition-all hover:shadow-md"
                      style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
                      onClick={() => handleViewOrder(order, order.orderType)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {isCartOrder ? '🛒' : isSubscription ? '📆' : '📅'}
                            </span>
                            <p className="font-medium" style={{ color: colors.text }}>
                              {isCartOrder 
                                ? `Order #${order.order_id}` 
                                : isSubscription 
                                  ? `Monthly Subscription #${order.id}` 
                                  : `Custom Order #${order.order_id || order.id}`}
                            </p>
                            {isCartOrder && order.payment && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ 
                                backgroundColor: getPaymentColor(order.payment) + '20',
                                color: getPaymentColor(order.payment)
                              }}>
                                {order.payment}
                              </span>
                            )}
                            {isSubscription && hasDiscount && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white">
                                {Math.round(((originalTotal - totalAmount) / originalTotal) * 100)}% Off
                              </span>
                            )}
                          </div>
                          <p className="text-xs" style={{ color: colors.textSecondary }}>
                            {formatDate(order.date || order.start_date || order.created_at)}
                          </p>
                          {isSubscription && order.end_date && (
                            <p className="text-xs" style={{ color: colors.textSecondary }}>
                              Ends: {formatDate(order.end_date)}
                            </p>
                          )}
                          <p className="text-xs" style={{ color: colors.textSecondary }}>
                            {orderItems.length} items
                          </p>
                          {isSubscription && mealOffs.length > 0 && (
                            <p className="text-xs flex items-center gap-1" style={{ color: '#F59E0B' }}>
                              <FaCalendarDay className="text-[10px]" />
                              {mealOffs.length} skipped meal{mealOffs.length > 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold" style={{ color: colors.primary }}>
                            ₹{totalAmount}
                          </p>
                          {isSubscription && hasDiscount && (
                            <p className="text-xs line-through" style={{ color: colors.textSecondary }}>
                              ₹{originalTotal}
                            </p>
                          )}
                          <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: getStatusColor(order.status) }}>
                            {getStatusText(order.status)}
                          </span>
                          {isSubscription && order.status !== 'cancelled' && (
                            <button
                              className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                              style={{ 
                                backgroundColor: colors.primary + '20',
                                color: colors.primary
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMealOffModal(order);
                              }}
                            >
                              <FaEdit className="text-[10px]" /> Modify
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Items Preview with Dynamic Images */}
                      {orderItems.length > 0 && (
                        <div className="mt-2 pt-2 border-t flex gap-2 flex-wrap" style={{ borderColor: colors.border }}>
                          {orderItems.slice(0, 4).map((item, idx) => {
                            const productId = parseInt(item.product_id);
                            const productImage = getProductImage(productId);
                            const productName = getProductName(productId);
                            
                            return (
                              <div 
                                key={idx} 
                                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full"
                                style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary }}
                              >
                                {productImage ? (
                                  <img 
                                    src={productImage}
                                    alt={productName}
                                    className="w-5 h-5 rounded-full object-cover"
                                    onError={(e) => {
                                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(productName)}&background=FF6B35&color=fff&size=32`;
                                    }}
                                  />
                                ) : (
                                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: colors.primary + '20' }}>
                                    <FaImage className="text-[10px]" style={{ color: colors.primary }} />
                                  </div>
                                )}
                                <span>{productName}</span>
                                {item.quantity && <span>×{item.quantity}</span>}
                              </div>
                            );
                          })}
                          {orderItems.length > 4 && (
                            <span className="text-xs px-2 py-1" style={{ color: colors.textSecondary }}>
                              +{orderItems.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-2 flex justify-end">
                        <button
                          className="text-xs flex items-center gap-1 px-3 py-1 rounded-lg transition-all hover:scale-105"
                          style={{ 
                            backgroundColor: colors.primary + '20',
                            color: colors.primary
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(order, order.orderType);
                          }}
                        >
                          <FaEye className="text-xs" /> View Details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Meal Off Modal */}
      <MealOffModal />
    </div>
  );
};

export default MyOrders;